import React, { useState, useEffect } from 'react';
import ClientProvider from '../../Various/ClientProvider';
import campaignSchema from "./campaignSchema";
import FullQualifiedClientId from "../../Various/FullQualifiedClientId";
import useStickyState from '../../../functions/useStickyState';

// Hooks
import { useBexioData } from './hooks/useBexioData';
import { useCampaignData } from './hooks/useCampaignData';

// Utils
import { getDynamicSchema, getUiSchema } from './utils/schemaUtils';

// Components
import CampaignsTree from './components/shared/CampaignsTree';
import PublishModal from './components/Details/PublishModal';
import NewCampaignModal from './components/shared/NewCampaignModal';
import OverviewTab from './components/Overview/OverviewTab';
import SendInvoicesTab from './components/SendInvoices/SendInvoicesTab';
import SendReportsTab from './components/SendReports/SendReportsTab';
import DetailsTab from './components/Details/DetailsTab';
import CampaignPerformanceTab from './components/CampaignPerformance/CampaignPerformanceTab';
import ActiveCampaignsPerformanceTab from './components/ActiveCampaignsPerformance/ActiveCampaignsPerformanceTab';

export default function WorkspaceCampaigns() {
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useStickyState(null, "campaignsSelectedCampaignId");
    const [activeTab, setActiveTab] = useStickyState('details', "campaignsActiveTab");
    const [expandedKeys, setExpandedKeys] = useStickyState([], "campaigns_expanded");
    const [tableValues, setTableValues] = useState({});
    const [invoicingFilter, setInvoicingFilter] = useState('active'); // 'active', 'not_active', 'both'
    const [tableRerenderKey, setTableRerenderKey] = useState(0);
    
    const wsProvider = React.useContext(ClientProvider);
    
    // Custom hooks
    const bexioData = useBexioData(wsProvider);
    const {
        campaignData,
        setCampaignData,
        clientCampaignsTree,
        setClientCampaignsTree,
        clientCampaignsOverview,
        setClientCampaignsOverview,
        loadCampaignData,
        saveCampaign,
        publishCampaign,
        loadAllCampaigns,
        loadAllCampaignsWithHistoryAndOpenBillingData,
        convertTimestampsToDates
    } = useCampaignData(wsProvider, wsProvider.selectedProjectId, wsProvider.selectedProjectPath, tableValues, setTableValues, activeTab);

    // Load campaign data when selectedCampaignId changes
    useEffect(() => {
        if (selectedCampaignId && clientCampaignsTree && Object.keys(clientCampaignsTree).length > 0) {
            // Find which client this campaign belongs to
            const campaignClient = findCampaignClient(selectedCampaignId);
            if (campaignClient && campaignClient !== wsProvider.clientSelected) {
                wsProvider.setClientSelected(campaignClient);
            }
            
            // Build the full client ID for the campaign's client
            const campaignClientData = wsProvider.clients.filter(x => x.client === campaignClient)[0];
            
            // If we can't find the client in wsProvider.clients, construct it from the project info
            let fullClientId = null;
            if (campaignClientData) {
                fullClientId = (new FullQualifiedClientId(campaignClientData["project"], campaignClientData["projectPath"], campaignClientData["client"])).toString();
            } else {
                // Fallback: construct from current project info
                fullClientId = (new FullQualifiedClientId(wsProvider.selectedProjectId, wsProvider.selectedProjectPath, campaignClient)).toString();
            }
            
            loadCampaignData(selectedCampaignId, fullClientId);
        } else if (selectedCampaignId && (!clientCampaignsTree || Object.keys(clientCampaignsTree).length === 0)) {
            // If we have a selectedCampaignId but no clientCampaignsTree yet, 
            // wait for clientCampaignsTree to load and then retry
        } else {
            setCampaignData(null);
        }
    }, [selectedCampaignId, clientCampaignsTree]);

    // Effect: When selecting a campaign, ensure the tree expands to show it
    useEffect(() => {
        if (selectedCampaignId && clientCampaignsTree && Object.keys(clientCampaignsTree).length > 0) {
            const campaignClient = findCampaignClient(selectedCampaignId);
            if (campaignClient && !expandedKeys.includes(campaignClient)) {
                setExpandedKeys([...expandedKeys, campaignClient]);
            }
        }
    }, [selectedCampaignId, clientCampaignsTree]);

    // Removed: Auto-switching tabs when selecting campaigns in tree
    // Users can now manually select which tab they want to view
    
    // Helper function to find which client a campaign belongs to
    const findCampaignClient = (campaignId) => {
        // Check tree data first (lightweight)
        for (const [clientId, clientArray] of Object.entries(clientCampaignsTree)) {
            if (clientArray[0]?.campaigns?.some(campaign => campaign.campaign_id === campaignId)) {
                return clientId;
            }
        }
        // Fallback to overview data if tree data doesn't have it
        for (const [clientId, clientArray] of Object.entries(clientCampaignsOverview)) {
            if (clientArray[0]?.campaigns?.some(campaign => campaign.campaign_id === campaignId)) {
                return clientId;
            }
        }
        return null;
    };

    // Schema and UI configuration
    const schema = getDynamicSchema(campaignSchema, bexioData, wsProvider);
    schema.properties.campaign_display_groups.items.properties.filter.options = { campaign: campaignData };
    
    const uiSchema = getUiSchema((filter, campaign, callback) => {
        wsProvider.ws.waitForSocketAndSend({
            action: "testCampaignFilterQuery",
            options: {
                filter: filter,
                campaign: campaign
            }
        }, (response) => { callback(response) });
    });

    // Event handlers
    const handlePublishClick = () => {
        setShowPublishModal(true);
    };

    const handlePublishConfirm = () => {
        setShowPublishModal(false);
        publishCampaign();
    };

    const onTreeSelect = (selectedKeys, info) => {
        if (selectedKeys.length > 0 && info.node.isLeaf) {
            setSelectedCampaignId(info.node.key);
        } else {
            setSelectedCampaignId(null);
        }
    };

    const handleNewCampaignClick = () => {
        setShowNewCampaignModal(true);
    };

    const handleNewCampaignSubmit = (newCampaignData) => {
        wsProvider.ws.waitForSocketAndSend({
            action: "addCampaign",
            options: {
                client_id: new FullQualifiedClientId(wsProvider.selectedProjectId, wsProvider.selectedProjectPath, newCampaignData.client_id).toString(),
                campaign_name: newCampaignData.campaign_name
            }
        }, (response) => {
            if (response.status === "success") {
                // Refresh the lightweight campaigns list for tree
                loadAllCampaigns();
                setSelectedCampaignId(response.campaign.campaign_id);
            }
        });
    };

    const handleTableRowClick = (row, columnKey, rowIdx, originalRow, extraParam) => {
        setSelectedCampaignId(row.campaign_id);
    };

    const handleTreeExpand = (expandedKeys) => {
        setExpandedKeys(expandedKeys);
    };

    const triggerTableRerender = () => {
        setTableRerenderKey(prev => prev + 1);
    };

    // Effect to trigger table rerender when invoicing filter changes
    useEffect(() => {
        triggerTableRerender();
    }, [invoicingFilter]);

    // Tab content renderer
    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return (
                    <OverviewTab
                        clientCampaigns={clientCampaignsOverview}
                        convertTimestampsToDates={convertTimestampsToDates}
                        onTableRowClick={handleTableRowClick}
                        tableValues={tableValues}
                        setTableValues={setTableValues}
                        invoicingFilter={invoicingFilter}
                        setInvoicingFilter={setInvoicingFilter}
                        tableRerenderKey={tableRerenderKey}
                        triggerTableRerender={triggerTableRerender}
                        loadAllCampaignsWithHistoryAndOpenBillingData={loadAllCampaignsWithHistoryAndOpenBillingData}
                    />
                );
            case 'send_invoices':
                return (
                    <SendInvoicesTab
                        clientCampaigns={clientCampaignsOverview}
                        convertTimestampsToDates={convertTimestampsToDates}
                        onTableRowClick={handleTableRowClick}
                        tableValues={tableValues}
                        setTableValues={setTableValues}
                        invoicingFilter={invoicingFilter}
                        setInvoicingFilter={setInvoicingFilter}
                        triggerTableRerender={triggerTableRerender}
                        tableRerenderKey={tableRerenderKey}
                        loadAllCampaignsWithHistoryAndOpenBillingData={loadAllCampaignsWithHistoryAndOpenBillingData}
                    />
                );
            case 'send_reports':
                return (
                    <SendReportsTab
                        clientCampaigns={clientCampaignsOverview}
                        convertTimestampsToDates={convertTimestampsToDates}
                        onTableRowClick={handleTableRowClick}
                        tableValues={tableValues}
                        setTableValues={setTableValues}
                        invoicingFilter={invoicingFilter}
                        setInvoicingFilter={setInvoicingFilter}
                        triggerTableRerender={triggerTableRerender}
                        tableRerenderKey={tableRerenderKey}
                        loadAllCampaignsWithHistoryAndOpenBillingData={loadAllCampaignsWithHistoryAndOpenBillingData}
                    />
                );
            case 'details':
                return (
                    <DetailsTab
                        campaignData={campaignData}
                        setCampaignData={setCampaignData}
                        schema={schema}
                        uiSchema={uiSchema}
                        onSave={saveCampaign}
                        onPublish={handlePublishClick}
                        wsProvider={wsProvider}
                    />
                );
            case 'campaign_performance':
                return (
                    <CampaignPerformanceTab
                        selectedCampaignId={selectedCampaignId}
                        wsProvider={wsProvider}
                        clientCampaignsTree={clientCampaignsTree}
                        clientCampaignsOverview={clientCampaignsOverview}
                    />
                );
            case 'active_campaigns_performance':
                return (
                    <ActiveCampaignsPerformanceTab
                        wsProvider={wsProvider}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="container-fluid">
            <div className="row g-0">
                <div className="col-2">
                    <CampaignsTree
                        clientCampaigns={clientCampaignsTree}
                        onSelect={onTreeSelect}
                        onNewCampaignClick={handleNewCampaignClick}
                        selectedClient={wsProvider.clientSelected}
                        selectedCampaignId={selectedCampaignId}
                        expandedKeys={expandedKeys}
                        onExpand={handleTreeExpand}
                    />
                </div>
                <div className="col-10">
                    <div className="row">
                        <div className="col-12 workspaceTop">
                            <div className='flex_header'>
                                <div className='campaign_details'>
                                    {/* Tab Navigation */}
                                    <ul className="nav nav-tabs mb-3">
                                        <li className="nav-item">
                                            <button 
                                                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('details')}
                                            >
                                                Campaign
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button 
                                                className={`nav-link ${activeTab === 'campaign_performance' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('campaign_performance')}
                                            >
                                                Campaign-Performance
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button 
                                                className={`nav-link ${activeTab === 'active_campaigns_performance' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('active_campaigns_performance')}
                                            >
                                                Active Campaigns Performance
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button 
                                                className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('overview')}
                                            >
                                                Overview
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button 
                                                className={`nav-link ${activeTab === 'send_invoices' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('send_invoices')}
                                            >
                                                Send Invoices
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeTab === 'send_reports' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('send_reports')}
                                            >
                                                Send Reports
                                            </button>
                                        </li>
                                    </ul>
                                    
                                    {/* Tab Content */}
                                    <div className="tab-content">
                                        {renderTabContent()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <PublishModal
                show={showPublishModal}
                onHide={() => setShowPublishModal(false)}
                onConfirm={handlePublishConfirm}
                campaign={campaignData}
            />

            <NewCampaignModal
                show={showNewCampaignModal}
                onHide={() => setShowNewCampaignModal(false)}
                onSubmit={handleNewCampaignSubmit}
                wsProvider={wsProvider}
            />
        </div>
    );
}